<template>
  <div class="page-wrapper">
    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">

        <div class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">Gestión de Convenciones</h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-database text-primary me-1"></i> {{ conventions.length }}
            </span>
          </div>
          <div class="d-flex gap-2">
            <button @click="modalNew" class="btn btn-primary btn-sm shadow-sm d-flex align-items-center">
              <i class="fas fa-plus-circle me-2"></i> Nueva Convención
            </button>
          </div>
        </div>

        <div class="card-body p-0">

          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" style="width: 3rem; height: 3rem" role="status">
              <span class="visually-hidden">Cargando...</span>
            </div>
            <p class="mt-3 text-muted">Cargando convenciones...</p>
          </div>

          <div v-else-if="conventions.length === 0" class="text-center py-5">
            <div class="text-secondary mb-3"><i class="fas fa-calendar-times fa-3x opacity-50"></i></div>
            <p class="text-muted">No se encontraron convenciones.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-3 border-0">Nombre</th>
                  <th class="py-3 border-0">Lugar</th>
                  <th class="py-3 border-0">Inicio</th>
                  <th class="py-3 border-0">Fin</th>
                  <th class="py-3 border-0">Estado</th>
                  <th class="py-3 border-0">Regionales</th>
                  <th class="py-3 border-0 text-center" style="white-space: nowrap;">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="convention in conventions" :key="convention.id">
                  <td class="ps-4 py-3">
                    <span class="fw-bold text-dark">{{ convention.name }}</span>
                  </td>
                  <td class="py-3">
                    <span class="badge bg-light text-dark border shadow-sm">
                      {{ convention.place || "No especificado" }}
                    </span>
                  </td>
                  <td class="py-3">
                    <span class="text-dark">{{ formattedDate(convention.start_datetime) }}</span>
                  </td>
                  <td class="py-3">
                    <span class="text-dark">{{ formattedDate(convention.end_datetime) }}</span>
                  </td>
                  <td class="py-3">
                    <span class="badge" :class="{
                      'bg-success': convention.status === 1,
                      'bg-danger': convention.status === 2,
                      'bg-secondary': convention.status !== 1 && convention.status !== 2,
                    }">
                      {{ convention.status === 1 ? "Activo" : convention.status === 2 ? "Inactivo" : convention.status === 3 ? "Completo" : "Otro" }}
                    </span>
                  </td>
                  <td class="py-3 text-secondary" style="max-width: 200px">
                    <div class="text-truncate small" :title="formatRegionals(convention.regionals)">
                      {{ formatRegionals(convention.regionals) || "No especificado" }}
                    </div>
                  </td>
                  <td class="py-3 text-center">
                    <div class="d-flex gap-2 justify-content-center">
                      <button class="btn btn-sm btn-icon btn-hover-light" title="Cantidad de inscriptos"
                        style="cursor: default" tabindex="-1">
                        <i class="bi bi-people text-primary fs-5"></i>
                        <span class="ms-2 badge bg-primary bg-opacity-25 text-primary">
                          {{ convention.participants_count || 0 }}
                        </span>
                      </button>
                      <button class="btn btn-sm btn-icon btn-hover-light"
                        @click="showConferences(convention.conferences, convention)"
                        :title="`Ver ${convention.conferences?.length || 0} jornadas`">
                        <i class="bi bi-calendar-event text-primary fs-5"></i>
                        <span class="ms-2 badge bg-primary bg-opacity-25 text-primary">
                          {{ convention.conferences?.length || 0 }}
                        </span>
                      </button>
                      <button class="btn btn-sm btn-icon btn-hover-light" @click="handleExport(convention)"
                        title="Exportar participantes del evento">
                        <i class="bi bi-download text-success fs-5"></i>
                      </button>
                      <button :disabled="convention.copy !== 1" class="btn btn-sm btn-icon btn-hover-light copy-btn"
                        @click="copyConvention(convention)" title="Duplicar convención">
                        <i class="fa fa-copy text-primary fs-5"></i>
                      </button>
                      <button class="btn btn-sm btn-icon btn-hover-light" @click="editConvention(convention)"
                        title="Editar convención">
                        <i class="bi bi-pencil-fill text-warning fs-5"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import conventionService from "@/services/api/convention";
import { useExporter } from "@/composables/useExporter";
import Swal from "sweetalert2";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";

const router = useRouter();
const route = useRoute();
const store = useStore();

const isLoading = ref(true);
const conventions = ref([]);

const { handleExport: exportData } = useExporter();

const formattedDate = (dateString) => {
  if (!dateString) return "No especificado";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatRegionals = (regionals) => {
  if (!regionals || !regionals.length) return "Sin regionales";

  const regionalNames = regionals.map((regional) => regional.name);
  return regionalNames.join(", ");
};

const modalNew = () => {
  router.push({ name: "NuevaConvencion" });
};

const fetchConventions = async () => {
  try {
    const response = await conventionService.getConventions();
    conventions.value = response.data.data;
  } catch (error) {
    console.error("Error obteniendo convenciones:", error);
  } finally {
    isLoading.value = false;
  }
};

const showConferences = (conferences, convention) => {
  store.dispatch("updateConferencesData", conferences);
  router.push({ name: "Conference", params: { id: convention.id } });
};

const handleExport = async (convention) => {
  try {
    const filename = await exportData(
      () => conventionService.exportConventionParticipants(convention.id),
      `participantes-${convention.place || "evento"}.csv`,
    );

    Swal.fire({
      icon: "success",
      title: "Exportación exitosa",
      text: `El archivo ${filename} se ha descargado correctamente`,
      confirmButtonColor: "#3085d6",
    });
  } catch (error) {
    console.error("Error exportando participantes:", error);
    Swal.fire({
      icon: "error",
      title: "Error en la exportación",
      text: "No se pudo generar el archivo de participantes",
      confirmButtonColor: "#d33",
    });
  }
};

const editConvention = (convention) => {
  router.push({ name: "EditarConvencion", params: { id: convention.id } });
};
const copyConvention = async (convention) => {
  const result = await Swal.fire({
    title: "¿Estás seguro?",
    text: "¿Querés duplicar esta convención?",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Sí, duplicar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
  });

  if (result.isConfirmed) {
    try {
      // Mostrar loading
      Swal.fire({
        title: "Duplicando convención...",
        text: "Por favor espera",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Llamar a la API para duplicar la convención
      await conventionService.duplicateConvention(convention.id);

      // Mostrar éxito
      await Swal.fire({
        icon: "success",
        title: "¡Convención duplicada!",
        text: "La convención se ha duplicado correctamente",
        confirmButtonColor: "#3085d6",
      });

      // Refrescar la tabla
      await fetchConventions();
    } catch (error) {
      console.error("Error duplicando convención:", error);
      Swal.fire({
        icon: "error",
        title: "Error al duplicar",
        text: "No se pudo duplicar la convención. Por favor intenta nuevamente.",
        confirmButtonColor: "#d33",
      });
    }
  }
};
onMounted(fetchConventions);

// Escuchar cambios en la URL para refrescar la tabla
watch(
  () => route.query.refresh,
  (newValue) => {
    if (newValue === "true") {
      fetchConventions();
      // Limpiar el parámetro de la URL
      router.replace({ query: {} });
    }
  },
);
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-card {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.copy-btn:disabled {
  background-color: transparent !important;
  border: none !important;
  opacity: 0.5;
  cursor: default;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
